import {
  TeamDto,
  TeamHierarchyDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Dictionary } from '@act/shared/types';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { stringComparer } from 'utilities/comparer';
import { GuideTableData } from './guide-table';

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
`;

// Styled Autocomplete - using 'as any' because styled-components don't preserve complex generics
const StyledAutocomplete = styled(Autocomplete)`
  flex-basis: 250px;
  flex-grow: 1;
  max-width: 400px;
` as any;

const stableFilterUserIds = (args: {
  allTableData: GuideTableData[];
  selectedTeams: TeamDto[];
  searchTerm: string;
}): GuideTableData[] => {
  let filteredData: GuideTableData[] = args.allTableData;
  // deal with teams
  if (args.selectedTeams.length > 0) {
    const teamUserIds = new Set();
    args.selectedTeams.forEach(t => {
      t.userIds.forEach(id => teamUserIds.add(id));
    });
    filteredData = filteredData.filter(gtd => teamUserIds.has(gtd.guideId));
  }
  // deal with the search term
  if (args.searchTerm.length > 0) {
    filteredData = filteredData.filter(gtd =>
      gtd.guideName.toLowerCase().includes(args.searchTerm.toLowerCase())
    );
  }
  return filteredData;
};

const GuideFilters = (props: {
  setFilteredGuideData: (data: GuideTableData[]) => void;
  hierarchy: TeamHierarchyDto[];
  userMap: Dictionary<UserDto>;
}) => {
  const { setFilteredGuideData, hierarchy, userMap } = props;
  const [teamOptions, setTeamOptions] = useState<TeamDto[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<TeamDto[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [allTableData, setAllTableData] = useState<GuideTableData[]>([]);

  useEffect(() => {
    if (!hierarchy) {
      return;
    }
    // Since each team is now its own "region", we can just flatten all teams
    const newTeamOptions: TeamDto[] = hierarchy.reduce((arr, region) => {
      return [...arr, ...region.teams];
    }, []);
    setTeamOptions(newTeamOptions);
  }, [hierarchy]);
  useEffect(() => {
    if (!hierarchy || !userMap) {
      return;
    }
    const userIdToTeamNames = {};
    hierarchy.forEach(r => {
      r.teams.forEach(t => {
        t.userIds.forEach(id => {
          if (id in userIdToTeamNames) {
            userIdToTeamNames[id].push(t.name);
          } else {
            userIdToTeamNames[id] = [t.name];
          }
        });
      });
    });
    const tableData: GuideTableData[] = Object.keys(userIdToTeamNames)
      .map(id => ({
        guideId: id,
        guideName: userMap[id]?.name || 'Unknown Guide',
        location: userIdToTeamNames[id].sort(stringComparer).join(', '),
      }))
      .filter(gtd => gtd.guideName !== 'Unknown Guide') // Filter out guides not in userMap
      .sort((a, b) => stringComparer(a.guideName, b.guideName));
    setAllTableData(tableData);
  }, [hierarchy, userMap]);

  useEffect(() => {
    const filteredTableData = stableFilterUserIds({
      allTableData,
      selectedTeams,
      searchTerm,
    });
    setFilteredGuideData(filteredTableData);
  }, [allTableData, selectedTeams, searchTerm, setFilteredGuideData]);

  const teamHandler = debounce(
    (_, val) => setSelectedTeams(val as TeamDto[]),
    250
  );
  const searchHandler = debounce(e => setSearchTerm(e.target.value), 250);

  return (
    <>
      <StyledTextField
        placeholder="Search by guide name"
        onChange={searchHandler}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <FilterContainer>
        <StyledAutocomplete
          multiple
          limitTags={2}
          onChange={teamHandler}
          options={teamOptions}
          getOptionLabel={(t: TeamDto) => t.name}
          renderInput={params => (
            <TextField {...params} label="Filter by team" placeholder="Team" />
          )}
        />
      </FilterContainer>
    </>
  );
};

export default GuideFilters;
